import {
  parseAdFormOAuthState,
  TRAFFICKING_PLATFORM_URN,
  type TraffickingPlatformUrnType,
} from '@mint-sdk/api-integrations/compat';

const GA4_PREFIX = 'connections:ga4';
const GA4_CONNECTION_NAME_KEY = `${GA4_PREFIX}:connectionName`;
const GA4_OAUTH_FLOW_ID_KEY = `${GA4_PREFIX}:oauthFlowId`;
const GA4_CONNECTION_ID_KEY = `${GA4_PREFIX}:connectionId`;

const CM_PREFIX = 'connections:cm';
const CM_CONNECTION_NAME_KEY = `${CM_PREFIX}:connectionName`;
const CM_OAUTH_FLOW_ID_KEY = `${CM_PREFIX}:oauthFlowId`;
const CM_PROFILE_ID_KEY = `${CM_PREFIX}:profileId`;
const CM_ADVERTISER_IDS_KEY = `${CM_PREFIX}:advertiserIds`;
const CM_CONNECTION_ID_KEY = `${CM_PREFIX}:connectionId`;

type ValidKeySuffix =
  | 'connectionName'
  | 'oauthFlowId'
  | 'profileId'
  | 'advertiserIds'
  | 'connectionId';

export const setGA4OauthFlow = (
  connectionName: string,
  oauthFlowId: string,
  connectionId?: string,
) => {
  localStorage.setItem(GA4_CONNECTION_NAME_KEY, connectionName);
  localStorage.setItem(GA4_OAUTH_FLOW_ID_KEY, oauthFlowId);
  localStorage.setItem(GA4_CONNECTION_ID_KEY, connectionId || '');
};

export const resetGA4OauthFlow = (item?: ValidKeySuffix) => {
  if (item) {
    localStorage.removeItem(`${GA4_PREFIX}:${item}`);
    return;
  }
  localStorage.removeItem(GA4_CONNECTION_NAME_KEY);
  localStorage.removeItem(GA4_OAUTH_FLOW_ID_KEY);
  localStorage.removeItem(GA4_CONNECTION_ID_KEY);
};

export const setCMOauthFlow = (
  connectionName: string,
  oauthFlowId: string,
  profileId?: string,
  advertiserIds?: string[],
  connectionId?: string,
) => {
  localStorage.setItem(CM_CONNECTION_NAME_KEY, connectionName);
  localStorage.setItem(CM_OAUTH_FLOW_ID_KEY, oauthFlowId);
  if (profileId) {
    localStorage.setItem(CM_PROFILE_ID_KEY, profileId);
  }
  if (advertiserIds) {
    localStorage.setItem(CM_ADVERTISER_IDS_KEY, JSON.stringify(advertiserIds));
  }
  localStorage.setItem(CM_CONNECTION_ID_KEY, connectionId || '');
};

export const resetCMOauthFlow = (item?: ValidKeySuffix) => {
  if (item) {
    localStorage.removeItem(`${CM_PREFIX}:${item}`);
    return;
  }
  localStorage.removeItem(CM_CONNECTION_NAME_KEY);
  localStorage.removeItem(CM_OAUTH_FLOW_ID_KEY);
  localStorage.removeItem(CM_PROFILE_ID_KEY);
  localStorage.removeItem(CM_ADVERTISER_IDS_KEY);
  localStorage.removeItem(CM_CONNECTION_ID_KEY);
};

export const getOauthFlowIdFrom = (
  platformName: TraffickingPlatformUrnType,
  data: string | null | undefined,
) => {
  if (platformName === TRAFFICKING_PLATFORM_URN.adform) {
    return data;
  }
  if (platformName === TRAFFICKING_PLATFORM_URN.googleAnalytics) {
    return localStorage.getItem(GA4_OAUTH_FLOW_ID_KEY);
  }
  if (platformName === TRAFFICKING_PLATFORM_URN.campaignManager) {
    return localStorage.getItem(CM_OAUTH_FLOW_ID_KEY);
  }
  return undefined;
};

export const getConnectionNameFrom = (
  platformName: TraffickingPlatformUrnType,
  state: string | null | undefined,
) => {
  if (platformName === TRAFFICKING_PLATFORM_URN.adform && state) {
    const parsedState = parseAdFormOAuthState(state);
    return parsedState?.integrationName;
  }
  if (platformName === TRAFFICKING_PLATFORM_URN.googleAnalytics) {
    return localStorage.getItem(GA4_CONNECTION_NAME_KEY);
  }
  if (platformName === TRAFFICKING_PLATFORM_URN.campaignManager) {
    return localStorage.getItem(CM_CONNECTION_NAME_KEY);
  }

  return undefined;
};

export const getConnectionIdFrom = (
  platformName: TraffickingPlatformUrnType,
) => {
  if (platformName === TRAFFICKING_PLATFORM_URN.googleAnalytics) {
    return localStorage.getItem(GA4_CONNECTION_ID_KEY) || undefined;
  }
  if (platformName === TRAFFICKING_PLATFORM_URN.campaignManager) {
    return localStorage.getItem(CM_CONNECTION_ID_KEY) || undefined;
  }
  return undefined;
};

export const getConnectionProfileIdFrom = (
  platformName: TraffickingPlatformUrnType,
) => {
  if (platformName === TRAFFICKING_PLATFORM_URN.campaignManager) {
    return localStorage.getItem(CM_PROFILE_ID_KEY) || undefined;
  }
  return undefined;
};

export const getAdvertiserIdsFrom = (
  platformName: TraffickingPlatformUrnType,
) => {
  if (platformName === TRAFFICKING_PLATFORM_URN.campaignManager) {
    const advertiserIds = localStorage.getItem(CM_ADVERTISER_IDS_KEY);
    return advertiserIds ? JSON.parse(advertiserIds) : [];
  }
  return [];
};
